"use client";

import type { Editor } from "@tiptap/react";
import type { EditorState } from "@tiptap/pm/state";
import type { CustomFont } from "@yedalabs/schema";

import { t } from "@/lib/localization";
import { FontSelector } from "@/features/brandKits/components/fontSelector";
import {
    FALLBACK_BODY_FONT,
    FALLBACK_TITLES_FONT,
} from "@/features/brandKits/lib/builtInFonts";
import { useLomdaV3Editor } from "../../../../store/useLomdaV3Editor";
import { DEFAULT_THEME } from "../../../../lib/theme";
import { nodeTag } from "../../../../lib/groupFormat";
import {
    HEADING_FIELD_CLASS,
    lineRole,
    type FontRole,
} from "../../../../lib/ownFonts";
import { usePopoverRefocus } from "./refocusEditor";

const EMPTY_FONTS: CustomFont[] = [];

export type FontReadout = {
    /** The font the selected text shares: null = none of its own, "" = mixed. */
    ownFont: string | null;
    /** The theme group its lines render in: null = titles and body mixed. */
    group: FontRole | null;
};

const readouts = new WeakMap<EditorState, FontReadout>();

/** What the font picker shows for the selection, or the whole line for a
 *  caret. Computed once per editor state. */
export function fontReadout(editor: Editor): FontReadout {
    const { state } = editor;
    const cached = readouts.get(state);
    if (cached) return cached;
    const headingField =
        editor.view.dom.classList.contains(HEADING_FIELD_CLASS);
    const { selection, doc } = state;
    const ranges = selection.empty
        ? [[selection.$from.start(), selection.$from.end()]]
        : selection.ranges.map((r) => [r.$from.pos, r.$to.pos]);
    let font: string | null | undefined;
    let group: FontRole | null | undefined;
    for (const [from, to] of ranges) {
        doc.nodesBetween(from, to, (node, pos) => {
            if (font === "" && group === null) return false;
            if (node.isTextblock) {
                // A line the selection only touches at its edge isn't in it.
                if (
                    !selection.empty &&
                    (pos + node.nodeSize - 1 <= from || pos + 1 >= to)
                )
                    return false;
                const role = lineRole(nodeTag(node) ?? "", headingField);
                group = group === undefined || group === role ? role : null;
                return font !== "";
            }
            if (!node.isText) return true;
            const ts = node.marks.find((m) => m.type.name === "textStyle");
            const f = (ts?.attrs.fontFamily as string | undefined) || null;
            font = font === undefined || font === f ? f : "";
            return false;
        });
    }
    const readout = {
        ownFont: font ?? null,
        group: group === undefined ? lineRole("", headingField) : group,
    };
    readouts.set(state, readout);
    return readout;
}

/**
 * Topbar font picker — the SAME shared `<FontSelector>` used in the brand kit
 * and the settings panel (built-ins + this lomda's uploads + "Upload custom
 * font"). Mirrors lomda V2's `FontFamilyControl`, wired to the V3 store.
 */
export function FontFamilyControl({ editor }: { editor: Editor }) {
    const customFonts =
        useLomdaV3Editor((s) => s.metadata.style?.customFonts) ?? EMPTY_FONTS;
    const addCustomFont = useLomdaV3Editor((s) => s.addCustomFont);
    const theme = useLomdaV3Editor((s) => s.metadata.theme) ?? DEFAULT_THEME;
    const popoverRefocus = usePopoverRefocus(editor);

    const titlesFont =
        theme.fontHeading || DEFAULT_THEME.fontHeading || FALLBACK_TITLES_FONT;
    const bodyFont =
        theme.fontBody || DEFAULT_THEME.fontBody || FALLBACK_BODY_FONT;

    const { ownFont, group } = fontReadout(editor);
    const themeFamily =
        group === "titles" ? titlesFont : group === "body" ? bodyFont : null;
    const displayFamily = ownFont ?? themeFamily ?? "";
    const matched = customFonts.find((f) => f.family === displayFamily);

    /** Empty selection → expand to the whole block (like the tag picker), then
     *  restore the caret. Real selection → apply to that range only. */
    const applyToBlockOrSelection = (family: string | null) => {
        const { from, to, $from, empty } = editor.state.selection;
        const chain = editor.chain().focus();
        const set = (c: ReturnType<typeof editor.chain>) =>
            family ? c.setFontFamily(family) : c.unsetFontFamily();
        if (empty) {
            const start = $from.start($from.depth);
            const end = $from.end($from.depth);
            set(chain.setTextSelection({ from: start, to: end }))
                .setTextSelection({ from, to })
                .run();
            return;
        }
        set(chain).run();
    };

    return (
        <FontSelector
            value={{ family: displayFamily, url: matched?.url ?? null }}
            onChange={(next) => {
                // The checked theme font's own row: the text already follows it.
                if (ownFont === null && next.family === themeFamily) return;
                applyToBlockOrSelection(next.family || null);
            }}
            customFonts={customFonts}
            onAddCustomFont={(font: CustomFont) => addCustomFont(font)}
            triggerClassName="h-8 w-[120px] shrink-0 px-3 text-sm hover:bg-background"
            themeFont={{
                label: themeFamily
                    ? t("lomda-v3-editor.format.theme-font", undefined, {
                          font: themeFamily,
                      })
                    : t("lomda-v3-editor.format.theme-font-mixed"),
                family: themeFamily ?? "",
                selected: ownFont === null && themeFamily !== null,
                onSelect: () => {
                    if (ownFont !== null) applyToBlockOrSelection(null);
                },
            }}
            {...popoverRefocus}
        />
    );
}
